// Phase 2.10 (hh-1hz) — AddRoomScreen.
//
// Manual fallback for creating a room. Ported from
// designproject/files/screens-forms.jsx (`AddRoomScreen`). Backend:
// POST /api/v1/rooms, scoped to a property picked from
// GET /api/v1/properties/tree. The design's suggestion chips are kept as a
// name-entry convenience.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, resolveIconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { rooms, humanizeError } from '../../api';
import { usePropertyTree } from './usePropertyTree';
import { PropertyPicker } from './PlacePicker';
import { IconPicker } from './IconPicker';
import { FormLabel, FormInput, FormFooter, FormError } from './formPrimitives';

const NAME_SUGGESTIONS: readonly string[] = [
  'Кухня',
  'Спальня',
  'Санузел',
  'Кладовка',
  'Гараж',
  'Терраса',
  'Прихожая',
  'Балкон',
];

export interface AddRoomScreenProps {
  readonly router: RouterApi;
  readonly propertyId?: string;
}

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

export function AddRoomScreen({ router, propertyId }: AddRoomScreenProps): JSX.Element {
  const tree = usePropertyTree();
  const [name, setName] = useState<string>('');
  const [selectedProperty, setSelectedProperty] = useState<string | null>(propertyId ?? null);
  // null = авто-подбор иконки по названию; строка = ручной выбор.
  const [icon, setIcon] = useState<string | null>(null);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const autoIcon = useMemo(() => resolveIconName('room', name), [name]);

  // Default to the first property once the tree resolves, unless the caller
  // pre-selected one or the user already picked.
  useEffect(() => {
    if (tree.status !== 'ready' || selectedProperty !== null) return;
    const first = tree.properties[0];
    if (first !== undefined) setSelectedProperty(first.id);
  }, [tree.status, tree.properties, selectedProperty]);

  const canSave = useMemo<boolean>(
    () => name.trim().length > 0 && selectedProperty !== null && !pending,
    [name, selectedProperty, pending],
  );

  const back = (): void => router.back();

  const save = (): void => {
    if (selectedProperty === null || name.trim().length === 0) return;
    setPending(true);
    setError(null);
    rooms
      .createRoom({
        name: name.trim(),
        propertyId: selectedProperty,
        ...(icon !== null ? { icon } : {}),
      })
      .then(() => {
        router.go('tree');
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader title="Новое помещение" subtitle="Заполни вручную" onBack={back} />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 16px' }}>
        <FormLabel>Название</FormLabel>
        <FormInput
          value={name}
          onChange={setName}
          placeholder="Кухня, спальня, гараж…"
          disabled={pending}
        />
        <div style={{ marginTop: 14, fontSize: 12.5, color: T.ink3 }}>
          Подсказки:
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {NAME_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                disabled={pending}
                onClick={() => setName(s)}
                style={{
                  padding: '6px 10px',
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 999,
                  fontFamily: T.font,
                  fontSize: 12,
                  color: T.ink,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <IconPicker
            value={icon}
            onChange={setIcon}
            autoIcon={autoIcon}
            disabled={pending}
          />
        </div>

        <FormLabel style={{ marginTop: 18 }}>Собственность</FormLabel>
        {tree.status === 'loading' && (
          <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>Загружаю…</div>
        )}
        {tree.status === 'error' && tree.error !== null && (
          <FormError message={tree.error} />
        )}
        {tree.status === 'ready' && (
          <PropertyPicker
            tree={tree.properties}
            value={selectedProperty}
            onChange={setSelectedProperty}
          />
        )}

        {error !== null && <FormError message={error} />}
      </div>
      <FormFooter
        onCancel={back}
        onConfirm={save}
        confirmLabel="Добавить"
        confirmIcon="plus"
        disabled={!canSave}
        pending={pending}
      />
    </div>
  );
}
